import { useEffect, useState } from 'react';

import { useRelayEnvironment } from 'react-relay';

import type { FomoCollaborator } from '../types';

import { fetchUserByAaid } from './fetchUserByAaid';

const MAX_AAIDS = 3;

export interface UseFomoCollaboratorsByAaidResult {
	collaborators: FomoCollaborator[];
	isReady: boolean;
}

/**
 * Resolve a list of Atlassian Account IDs (AAIDs) to `FomoCollaborator[]`
 * so the FOMO nudge can render real names + avatars.
 *
 * - Caps input at 3 AAIDs (the avatar group's max display count).
 * - Returns `{ collaborators: [], isReady: true }` immediately when disabled
 *   or no AAIDs were supplied (so callers can safely render `null`).
 * - Returns `{ collaborators: [], isReady: false }` while fetching, then
 *   `{ collaborators: [...], isReady: true }` once all requests settle.
 * - Errors and missing users are silently dropped from the result list.
 *
 * The hook does no caching or de-duplication beyond a single resolver
 * cycle. Each invocation re-fetches when the AAID list changes.
 */
export function useFomoCollaboratorsByAaid(
	aaids: ReadonlyArray<string>,
	{ enabled = true }: { enabled?: boolean } = {},
): UseFomoCollaboratorsByAaidResult {
	const environment = useRelayEnvironment();
	const capped = aaids.slice(0, MAX_AAIDS);
	const isNoop = !enabled || capped.length === 0;

	const [state, setState] = useState<UseFomoCollaboratorsByAaidResult>({
		collaborators: [],
		isReady: isNoop,
	});

	// Build a stable dependency key so the effect only re-runs when the
	// effective input changes. Use JSON.stringify to avoid collisions if
	// an AAID ever contains the previously-used `|` delimiter.
	const key = isNoop ? '__noop__' : JSON.stringify(capped);

	useEffect(() => {
		if (isNoop) {
			setState({ collaborators: [], isReady: true });
			return;
		}

		let cancelled = false;
		setState({ collaborators: [], isReady: false });

		// `fetchUserByAaid` never rejects (catches its own errors and resolves
		// to `null`), so `Promise.all` here is guaranteed to resolve — no catch
		// branch needed.
		Promise.all(capped.map((aaid) => fetchUserByAaid(environment, aaid))).then((results) => {
			if (cancelled) return;
			const collaborators = results.filter(
				(c): c is FomoCollaborator => c !== null,
			);
			setState({ collaborators, isReady: true });
		});

		return () => {
			cancelled = true;
		};
		// `capped` is recomputed each render but `key` captures its identity.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key, isNoop, environment]);

	return state;
}
